/*
 * product-custom-purchase-rosehip.js
 * Modeled on product-custom-purchase-yummies.js.
 *
 * Single option (option1 = Value Packs): visible values 1-Pack / 2-Pack.
 * Hidden pack variants 3-Pack / 6-Pack / 12-Pack back the 3-month
 * subscription and the Buy Once pack grid (shown when 2-Pack is selected).
 *
 * Base price = bottles × $65.
 * Discount % = (base - actual) / base × 100, rounded.
 */

window.Shopify = window.Shopify || {};
window.Shopify.formatMoney =
  window.Shopify.formatMoney ||
  function (cents) {
    if (typeof cents === "string") cents = cents.replace(".", "");
    const amount = cents / 100;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

// Show two decimals only when the price has non-zero cents (e.g. "$52.50"),
// otherwise drop them (e.g. "$65"). Keeps half-dollar plan prices honest
// instead of rounding them to the nearest whole dollar.
const formatRosehipPrice = (cents) => {
  let value = cents;
  if (typeof value === "string") value = value.replace(".", "");
  value = Number(value) || 0;
  const hasCents = value % 100 !== 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: hasCents ? 2 : 0,
  }).format(value / 100);
};

document.addEventListener("DOMContentLoaded", () => {
  const FREE_SHIPPING_THRESHOLD_CENTS = 10000;
  const DEFAULT_SHIPPING_TEXT = "$6.95 Shipping";
  // Rosehip runs a single pack track (1-Pack); the top selector is removed.
  // The pack grid (1/2/3-Pack) is always visible inside Buy Once.
  const DEFAULT_PACK = "1-Pack";

  // Base price per bottle = $65 = 6500 cents
  const BASE_PRICE_PER_BOTTLE_CENTS = 6500;

  // Read dynamic selling plan data injected by Liquid
  let sellingPlansData = {};
  const spScript = document.getElementById("rosehip-selling-plans");
  if (spScript) {
    try {
      sellingPlansData = JSON.parse(spScript.textContent);
    } catch (e) {
      console.warn("Could not parse rosehip selling plans", e);
    }
  }

  // Read hardcoded plan ID map injected by Liquid
  let planMap = {};
  const pmScript = document.getElementById("rosehip-plan-map");
  if (pmScript) {
    try {
      planMap = JSON.parse(pmScript.textContent);
    } catch (e) {
      console.warn("Could not parse rosehip plan map", e);
    }
  }

  const parsePackBottles = (packValue) => {
    if (!packValue) return 0;
    const m = String(packValue).match(/(\d+)/);
    return m ? parseInt(m[1], 10) : 0;
  };

  const packLabelFromBottles = (bottles) => `${bottles}-Pack`;

  const getFormulaBasePrice = (packValue) =>
    parsePackBottles(packValue) * BASE_PRICE_PER_BOTTLE_CENTS;

  const bottlesLabel = (n) => (n === 1 ? "1 bottle" : `${n} bottles`);

  // DOM references
  const productForm = document.querySelector(".product-single__form");
  if (!productForm) return;

  const formContainer =
    productForm.closest('[data-section-type="product-template"]') ||
    productForm.closest("[data-product-container]");
  const sectionId = formContainer ? formContainer.dataset.sectionId : null;
  const productJSONScript = formContainer
    ? formContainer.querySelector(`#ProductJson-${sectionId}`)
    : document.querySelector(
        'script[type="application/json"][data-product-json]',
      );

  const productData = productJSONScript
    ? JSON.parse(productJSONScript.textContent)
    : window.customProductData;

  const productContainer = document.querySelector(
    ".custom-subscription-widget-container.rosehip-widget",
  );
  const subscriptionIdInput = productForm.querySelector(
    'input[name="selling_plan"]',
  );
  const variantIdInput = productForm.querySelector('[name="id"]');

  const btnTextElement = document.querySelector(".btn--add-to-cart .btn__text");
  const shippingPriceElement = document.querySelector(
    "#buy-once-option .shipping-price",
  );

  const purchaseRadios = productContainer
    ? productContainer.querySelectorAll('input[name="purchase_option"]')
    : [];

  let selectedPack = DEFAULT_PACK;
  let selectedBuyOncePack = DEFAULT_PACK;

  const packButtons = productContainer
    ? productContainer.querySelectorAll(".pack-button")
    : [];
  const packGrid = productContainer
    ? productContainer.querySelector("[data-pack-selector]")
    : null;
  const threeMonthLabel = productContainer
    ? productContainer.querySelector(".purchase-option-label--three-month")
    : null;

  // --- CORE FUNCTIONS ---

  // Match a variant by pack value. Tries exact option1 first, then a
  // case-insensitive match, then a numeric bottles match against any
  // option slot — so "6-Pack" / "6 Pack" / "6-pack" / "6" all resolve.
  const getVariantByPack = (packValue) => {
    if (!productData || !packValue) return null;
    const lower = String(packValue).toLowerCase();
    const targetBottles = parsePackBottles(packValue);
    const optionContainsBottles = (opt) =>
      opt != null && parsePackBottles(opt) === targetBottles;
    return (
      productData.variants.find((v) => v.option1 === packValue) ||
      productData.variants.find(
        (v) => v.option1 && v.option1.toLowerCase() === lower,
      ) ||
      (targetBottles > 0
        ? productData.variants.find(
            (v) =>
              optionContainsBottles(v.option1) ||
              optionContainsBottles(v.option2) ||
              optionContainsBottles(v.option3),
          )
        : null) ||
      null
    );
  };

  const getSellingPlanByPlanId = (variantId, planId) => {
    const vData = sellingPlansData[variantId];
    if (!vData || !vData.allocations) return null;
    return vData.allocations.find((a) => a.plan_id === planId) || null;
  };

  const getTargetData = (isSubscription, packValue, planId) => {
    let price = 0;
    let compareAt = 0;
    let sellingPlan = "";

    const targetVariant = getVariantByPack(packValue);
    if (!targetVariant)
      return { variant: null, price: 0, compareAt: 0, sellingPlan: "" };

    const formulaBase = getFormulaBasePrice(packValue);

    if (isSubscription) {
      const allocation = planId
        ? getSellingPlanByPlanId(targetVariant.id, planId)
        : null;
      if (allocation && allocation.price > 0) {
        sellingPlan = allocation.plan_id;
        price = allocation.price;
      } else if (
        targetVariant.selling_plan_allocations &&
        targetVariant.selling_plan_allocations.length > 0
      ) {
        const alloc = targetVariant.selling_plan_allocations[0];
        sellingPlan = alloc.selling_plan_id;
        price =
          alloc.price_adjustments && alloc.price_adjustments[0]
            ? alloc.price_adjustments[0].price
            : targetVariant.price;
      } else {
        price = targetVariant.price;
      }
      compareAt = formulaBase;
    } else {
      price = targetVariant.price;
      compareAt = formulaBase;
    }

    return {
      variant: targetVariant,
      price: price,
      compareAt: compareAt,
      sellingPlan: sellingPlan.toString(),
    };
  };

  const updateBuyOnceLinkPrice = () => {
    const el = document.querySelector(".js-buy-once-link-price");
    if (!el || !selectedPack) return;
    // Link mirrors the Buy Once price for the currently selected top pack
    const vd = getTargetData(false, selectedPack, null);
    if (vd.variant) {
      el.textContent = formatRosehipPrice(vd.price);
    }
  };

  // --- PACK GRID LOGIC ---

  // The pack grid (1/2/3-Pack) is always present inside Buy Once now, so we
  // simply return whichever pill the customer last clicked.
  const getEffectiveBuyOncePack = () =>
    selectedBuyOncePack || DEFAULT_PACK;

  const updatePackGridVisibility = () => {
    if (!packGrid) return;
    packGrid.classList.add("is-visible");
    packGrid.style.display = "flex";
  };

  // Refresh every "Save X%" pill rendered in the pack grid. Iterates over
  // the actual DOM so we pick up whatever pack columns are present.
  const updatePackBadges = () => {
    document
      .querySelectorAll(".pack-save-badge[data-pack-save-badge]")
      .forEach((badge) => {
        const packValue = badge.dataset.packSaveBadge;
        const variant = getVariantByPack(packValue);
        if (!variant) {
          badge.textContent = "";
          return;
        }
        const base = getFormulaBasePrice(packValue);
        const price = variant.price;
        if (base > price && base > 0) {
          const pct = Math.ceil(((base - price) / base) * 100);
          badge.textContent = "Save " + pct + "%";
        } else {
          badge.textContent = "";
        }
      });
  };

  // 3-month card is always Most Popular in the single-pack rosehip flow.
  const updateMostPopularBanner = () => {
    if (!threeMonthLabel) return;
    threeMonthLabel.classList.add("is-most-popular");
  };

  const updateBuyOnceCountLabel = () => {
    const el = document.querySelector(".js-buy-once-count");
    if (!el) return;
    el.textContent = bottlesLabel(parsePackBottles(getEffectiveBuyOncePack()));
  };

  const updateSubscriptionDeliveryLabels = () => {
    const monthlyEl = document.querySelector(".js-monthly-bottles-display");
    if (monthlyEl) {
      monthlyEl.textContent = bottlesLabel(parsePackBottles(selectedPack));
    }
    const threeMonthEl = document.querySelector(
      ".js-three-month-bottles-display",
    );
    if (threeMonthEl) {
      const monthlyBottles = parsePackBottles(selectedPack);
      threeMonthEl.textContent = bottlesLabel(monthlyBottles * 3);
    }
  };

  // Monthly free-shipping line toggles based on the monthly plan price for the
  // currently selected top pack. Threshold matches the Buy Once one ($100).
  const updateMonthlyFreeShipping = () => {
    const el = document.querySelector(".js-monthly-free-shipping");
    if (!el) return;
    const packPlans = planMap[selectedPack];
    const monthlyPlanId = packPlans ? packPlans.monthly : null;
    const data = getTargetData(true, selectedPack, monthlyPlanId);
    const showFree =
      data.variant && data.price >= FREE_SHIPPING_THRESHOLD_CENTS;
    el.style.display = showFree ? "" : "none";
  };

  // 3-month free-shipping line — same threshold ($100). The 3-month plan
  // ships a 3× pack, so we look up the multiplied pack variant.
  const updateThreeMonthFreeShipping = () => {
    const el = document.querySelector(".js-three-month-free-shipping");
    if (!el) return;
    const packPlans = planMap[selectedPack];
    const threeMonthPlanId = packPlans ? packPlans.threeMonth : null;
    const threeMonthPack = packLabelFromBottles(
      parsePackBottles(selectedPack) * 3,
    );
    const data = getTargetData(true, threeMonthPack, threeMonthPlanId);
    const showFree =
      data.variant && data.price >= FREE_SHIPPING_THRESHOLD_CENTS;
    el.style.display = showFree ? "" : "none";
  };

  // --- MAIN UPDATE ---
  const updateFormAndPrices = () => {
    if (!productContainer || !selectedPack) return;
    const selectedRadio = productContainer.querySelector(
      'input[name="purchase_option"]:checked',
    );
    if (!selectedRadio) return;

    const purchaseOptionValue = selectedRadio.value;
    const isSubscription = purchaseOptionValue !== "onetime";
    const countMultiplier =
      parseInt(selectedRadio.dataset.countMultiplier, 10) || 1;

    // Resolve which variant this purchase option uses.
    let effectivePack;
    if (!isSubscription) {
      effectivePack = getEffectiveBuyOncePack();
    } else if (countMultiplier > 1) {
      effectivePack = packLabelFromBottles(
        parsePackBottles(selectedPack) * countMultiplier,
      );
    } else {
      effectivePack = selectedPack;
    }

    // Resolve the correct plan ID for the selected purchase option.
    let activePlanId = null;
    if (isSubscription) {
      const packPlans = planMap[selectedPack];
      if (packPlans) {
        activePlanId =
          countMultiplier > 1 ? packPlans.threeMonth : packPlans.monthly;
      }
    }

    const finalData = getTargetData(isSubscription, effectivePack, activePlanId);

    // Only mutate the cart form when we resolved a real variant. Display
    // updates still run so labels / badges reflect the new selection even
    // if a variant is missing in Shopify.
    if (finalData.variant) {
      if (variantIdInput) {
        variantIdInput.value = finalData.variant.id;
        variantIdInput.dispatchEvent(new Event("change", { bubbles: true }));
      }
      if (subscriptionIdInput) {
        subscriptionIdInput.value = finalData.sellingPlan;
      }
    }

    updateAllPurchaseOptionPrices();
    updatePackGridVisibility();
    updatePackBadges();
    updateMostPopularBanner();
    updateBuyOnceCountLabel();
    updateSubscriptionDeliveryLabels();
    updateMonthlyFreeShipping();
    updateThreeMonthFreeShipping();

    if (btnTextElement && finalData.variant) {
      btnTextElement.textContent = finalData.variant.available
        ? "ADD TO CART"
        : "SOLD OUT";
    }

    updateBuyOnceLinkPrice();

    if (shippingPriceElement && purchaseOptionValue === "onetime") {
      if (finalData.price >= FREE_SHIPPING_THRESHOLD_CENTS) {
        shippingPriceElement.textContent = "Free Shipping";
        shippingPriceElement.style.fontWeight = "bold";
      } else {
        shippingPriceElement.textContent = DEFAULT_SHIPPING_TEXT;
        shippingPriceElement.style.fontWeight = "normal";
      }
    }

    // Keep selling plan IDs on every radio fresh for the current top pack.
    const packPlans = planMap[selectedPack];
    productContainer
      .querySelectorAll('input[name="purchase_option"]')
      .forEach((radio) => {
        if (radio.value === "onetime") return;
        const mult = parseInt(radio.dataset.countMultiplier, 10) || 1;
        const radioPack =
          mult > 1
            ? packLabelFromBottles(parsePackBottles(selectedPack) * mult)
            : selectedPack;
        const radioVariant = getVariantByPack(radioPack);
        if (!radioVariant) return;
        const targetPlanId = packPlans
          ? mult > 1
            ? packPlans.threeMonth
            : packPlans.monthly
          : null;
        const allocation = targetPlanId
          ? getSellingPlanByPlanId(radioVariant.id, targetPlanId)
          : null;
        if (allocation) {
          radio.dataset.sellingPlanId = allocation.plan_id;
          radio.value = "subscription-" + allocation.plan_id;
        }
      });
  };

  const updateAllPurchaseOptionPrices = () => {
    // Buy Once price — uses effective pack from pack grid
    const effectiveBuyOncePack = getEffectiveBuyOncePack();
    const buyOnceLabel = document.querySelector("#buy-once-option");
    if (buyOnceLabel) {
      const data = getTargetData(false, effectiveBuyOncePack, null);
      if (data.variant) {
        const formulaBase = getFormulaBasePrice(effectiveBuyOncePack);
        fillPriceDetails(buyOnceLabel, data.price, data.compareAt, formulaBase, 1);
      }
    }

    // Subscription prices — each radio may swap to a multiplied pack variant
    if (!productContainer) return;
    const packPlans = planMap[selectedPack];
    productContainer
      .querySelectorAll(".purchase-option-label:not(.purchase-option-onetime)")
      .forEach((label) => {
        const radioInput = label.querySelector('input[name="purchase_option"]');
        const mult = radioInput
          ? parseInt(radioInput.dataset.countMultiplier, 10) || 1
          : 1;
        const subPack =
          mult > 1
            ? packLabelFromBottles(parsePackBottles(selectedPack) * mult)
            : selectedPack;
        const targetPlanId = packPlans
          ? mult > 1
            ? packPlans.threeMonth
            : packPlans.monthly
          : null;
        const data = getTargetData(true, subPack, targetPlanId);
        if (data.variant) {
          const formulaBase = getFormulaBasePrice(subPack);
          fillPriceDetails(label, data.price, data.compareAt, formulaBase, mult);
        }
      });
  };

  // Subscription boxes show the price PER MONTH: the plan total divided by the
  // number of months it covers (Monthly = 1, 3-Month Delivery = 3), suffixed
  // "/mo". The strikethrough base is divided the same way so the "Save X%"
  // badge — computed from the undivided figures — stays unchanged.
  const PER_MONTH_SUFFIX =
    ' / <span style="font-weight:500;font-size:0.8em;">mo</span>';

  const fillPriceDetails = (label, price, compare, formulaBase, months) => {
    const pDisplay = label.querySelector(".current-price-display");
    const cDisplay = label.querySelector(".compare-at-price-display");
    const sLabel = label.querySelector(".savings-label");
    const base = formulaBase || compare;

    if (label.classList.contains("purchase-option-onetime")) {
      if (pDisplay) pDisplay.textContent = formatRosehipPrice(price);
      if (cDisplay) {
        if (base > price) {
          cDisplay.textContent = formatRosehipPrice(base);
          cDisplay.style.display = "inline";
          cDisplay.style.visibility = "visible";
        } else {
          cDisplay.style.display = "none";
          cDisplay.style.visibility = "hidden";
        }
      }
      if (sLabel) sLabel.style.display = "none";
      return;
    }

    const divisor = months && months > 1 ? months : 1;
    const perMonthPrice = Math.round(price / divisor);
    const perMonthBase = Math.round(base / divisor);

    if (pDisplay) {
      pDisplay.innerHTML = formatRosehipPrice(perMonthPrice) + PER_MONTH_SUFFIX;
    }
    if (cDisplay) {
      if (base > price) {
        cDisplay.textContent = formatRosehipPrice(perMonthBase);
        cDisplay.style.display = "inline";
        cDisplay.style.visibility = "visible";
      } else {
        cDisplay.style.display = "none";
        cDisplay.style.visibility = "hidden";
      }
    }
    if (sLabel) {
      if (base > price && base > 0) {
        const pct = Math.ceil(((base - price) / base) * 100);
        sLabel.textContent = `Save ${pct}%`;
        sLabel.style.display = "inline-block";
      } else {
        sLabel.style.display = "none";
      }
    }
  };

  const handlePurchaseOptionChange = (e) => {
    const selectedRadio = e.target;

    purchaseRadios.forEach((radio) => {
      radio.closest("label").classList.remove("is-selected");
    });
    selectedRadio.closest("label").classList.add("is-selected");

    if (selectedRadio.value === "onetime") {
      const buyOnceBox = document.getElementById("buy-once-option");
      if (buyOnceBox) {
        buyOnceBox.style.display = "block";
      }
    }

    updateFormAndPrices();
  };

  // --- LISTENERS ---

  packButtons.forEach((button) => {
    button.addEventListener("click", (e) => {
      e.preventDefault();
      packButtons.forEach((btn) => btn.classList.remove("is-selected-pack"));
      button.classList.add("is-selected-pack");
      selectedBuyOncePack = button.dataset.packValue;
      updateBuyOnceCountLabel();
      updateFormAndPrices();
    });
  });

  purchaseRadios.forEach((radio) => {
    radio.addEventListener("change", handlePurchaseOptionChange);
  });

  // --- INIT ---
  const checkedRadio = document.querySelector(
    'input[name="purchase_option"]:checked',
  );
  if (checkedRadio) {
    setTimeout(() => checkedRadio.dispatchEvent(new Event("change")), 100);
  } else {
    updateFormAndPrices();
  }
});
